import { Check, Mail, Pencil, Phone, Star, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/CopyButton'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { parseError } from '@/lib/errors'
import { formatPhone } from '@/lib/formatters'
import {
  type LeadContact,
  useDeleteContact,
  useSetPrimaryContact,
  useUpdateContact,
} from '@/models/salesLead'

interface ContactRowProps {
  leadId: string
  contact: LeadContact
  isOnly: boolean
}

export function ContactRow({ leadId, contact, isOnly }: ContactRowProps) {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const updateMutation = useUpdateContact(leadId)
  const deleteMutation = useDeleteContact(leadId)
  const setPrimaryMutation = useSetPrimaryContact(leadId)

  const isPending = updateMutation.isPending || deleteMutation.isPending || setPrimaryMutation.isPending

  function startEdit(field: string, current: string) {
    setEditingField(field)
    setDraft(current)
  }

  function cancelEdit() {
    setEditingField(null)
    setDraft('')
  }

  function saveField(field: string) {
    updateMutation.mutate(
      { contactId: contact.id, [field]: draft.trim() },
      {
        onSuccess: () => {
          setEditingField(null)
          setDraft('')
        },
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  function removeSecondaryEmail(email: string) {
    updateMutation.mutate(
      { contactId: contact.id, secondaryEmails: contact.secondaryEmails.filter((e) => e !== email) },
      { onError: (err) => toast.error(parseError(err)) }
    )
  }

  function removeSecondaryPhone(phone: string) {
    updateMutation.mutate(
      { contactId: contact.id, secondaryPhones: contact.secondaryPhones.filter((p) => p !== phone) },
      { onError: (err) => toast.error(parseError(err)) }
    )
  }

  return (
    <div className="space-y-2">
      {/* Name row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {editingField === 'name' ? (
            <span className="inline-flex items-center gap-1">
              <Input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveField('name')
                  if (e.key === 'Escape') cancelEdit()
                }}
                className="h-7 px-2 py-0 text-sm"
                autoFocus
                disabled={isPending}
              />
              <Button
                variant="iconOnly"
                size="sm"
                className="size-6 text-green-600 hover:bg-green-50"
                onClick={() => saveField('name')}
                disabled={isPending}
              >
                {isPending ? <Spinner className="size-3" /> : <Check className="size-3" />}
              </Button>
              <Button
                variant="iconOnly"
                size="sm"
                className="text-muted-foreground size-6 hover:bg-neutral-100"
                onClick={cancelEdit}
                disabled={isPending}
              >
                <X className="size-3" />
              </Button>
            </span>
          ) : (
            <button
              className="truncate text-sm font-medium hover:underline"
              onClick={() => startEdit('name', contact.name)}
            >
              {contact.name || <span className="text-muted-foreground italic">Add name</span>}
            </button>
          )}
          {contact.isPrimary && (
            <Badge variant="secondary" className="shrink-0 text-xs">
              Primary
            </Badge>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!contact.isPrimary && (
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground size-6 hover:text-amber-500"
              title="Set as primary"
              onClick={() =>
                setPrimaryMutation.mutate(contact.id, {
                  onError: (err) => toast.error(parseError(err)),
                })
              }
              disabled={isPending}
            >
              <Star className="size-3" />
            </Button>
          )}
          {!isOnly && (
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground size-6 hover:bg-red-50 hover:text-red-600"
              title="Delete contact"
              onClick={() =>
                deleteMutation.mutate(contact.id, {
                  onError: (err) => toast.error(parseError(err)),
                })
              }
              disabled={isPending}
            >
              <Trash2 className="size-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Primary email */}
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <Mail className="text-muted-foreground size-3 shrink-0" />
        {editingField === 'primaryEmail' ? (
          <span className="inline-flex items-center gap-1">
            <Input
              type="email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveField('primaryEmail')
                if (e.key === 'Escape') cancelEdit()
              }}
              className="h-7 px-2 py-0 text-sm"
              autoFocus
              disabled={isPending}
            />
            <Button
              variant="iconOnly"
              size="sm"
              className="size-6 text-green-600 hover:bg-green-50"
              onClick={() => saveField('primaryEmail')}
              disabled={isPending}
            >
              {isPending ? <Spinner className="size-3" /> : <Check className="size-3" />}
            </Button>
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground size-6 hover:bg-neutral-100"
              onClick={cancelEdit}
              disabled={isPending}
            >
              <X className="size-3" />
            </Button>
          </span>
        ) : contact.primaryEmail ? (
          <span className="inline-flex min-w-0 items-center gap-1">
            <a
              href={`mailto:${contact.primaryEmail}`}
              className="break-all text-blue-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {contact.primaryEmail}
            </a>
            <CopyButton value={contact.primaryEmail} />
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground hover:text-foreground size-4"
              onClick={() => startEdit('primaryEmail', contact.primaryEmail)}
            >
              <Pencil className="size-3" />
            </Button>
          </span>
        ) : (
          <button
            className="text-muted-foreground hover:text-foreground italic"
            onClick={() => startEdit('primaryEmail', '')}
          >
            Add email
          </button>
        )}
      </div>

      {/* Secondary emails */}
      {contact.secondaryEmails.map((email) => (
        <div key={email} className="flex min-w-0 items-center gap-2 pl-5 text-sm">
          <a href={`mailto:${email}`} className="break-all text-blue-600 hover:underline">
            {email}
          </a>
          <CopyButton value={email} />
          <Button
            variant="iconOnly"
            size="sm"
            className="text-muted-foreground size-4 hover:bg-red-50 hover:text-red-600"
            onClick={() => removeSecondaryEmail(email)}
            disabled={isPending}
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}

      {/* Primary phone */}
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <Phone className="text-muted-foreground size-3 shrink-0" />
        {editingField === 'primaryPhone' ? (
          <span className="inline-flex items-center gap-1">
            <Input
              type="tel"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveField('primaryPhone')
                if (e.key === 'Escape') cancelEdit()
              }}
              className="h-7 px-2 py-0 text-sm"
              autoFocus
              disabled={isPending}
            />
            <Button
              variant="iconOnly"
              size="sm"
              className="size-6 text-green-600 hover:bg-green-50"
              onClick={() => saveField('primaryPhone')}
              disabled={isPending}
            >
              {isPending ? <Spinner className="size-3" /> : <Check className="size-3" />}
            </Button>
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground size-6 hover:bg-neutral-100"
              onClick={cancelEdit}
              disabled={isPending}
            >
              <X className="size-3" />
            </Button>
          </span>
        ) : contact.primaryPhone ? (
          <span className="inline-flex items-center gap-1">
            <a href={`tel:${contact.primaryPhone}`} className="text-blue-600 hover:underline">
              {formatPhone(contact.primaryPhone)}
            </a>
            <CopyButton value={contact.primaryPhone} />
            <Button
              variant="iconOnly"
              size="sm"
              className="text-muted-foreground hover:text-foreground size-4"
              onClick={() => startEdit('primaryPhone', contact.primaryPhone)}
            >
              <Pencil className="size-3" />
            </Button>
          </span>
        ) : (
          <button
            className="text-muted-foreground hover:text-foreground italic"
            onClick={() => startEdit('primaryPhone', '')}
          >
            Add phone
          </button>
        )}
      </div>

      {/* Secondary phones */}
      {contact.secondaryPhones.map((phone) => (
        <div key={phone} className="flex min-w-0 items-center gap-2 pl-5 text-sm">
          <a href={`tel:${phone}`} className="text-blue-600 hover:underline">
            {formatPhone(phone)}
          </a>
          <CopyButton value={phone} />
          <Button
            variant="iconOnly"
            size="sm"
            className="text-muted-foreground size-4 hover:bg-red-50 hover:text-red-600"
            onClick={() => removeSecondaryPhone(phone)}
            disabled={isPending}
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}
    </div>
  )
}
